import { getRequest } from './baseRequests/getRequest'
import {
  ApiEmployerBenefitProgram,
  BenefitProgramUtiilizationResponse,
  BenefitProgramUtilizationApiResponse,
  EmployerBenefitProgramsApiResponse,
  EmployerBenefitProgramsResponse,
  RequestResponse
} from './apiResponseTypes'
import { EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { BenefitProgramPolicy } from 'Utils/types/benefitProgramPolicy'
import { parseServerDate } from 'Utils/helpers/dateUtils'
import { BasicBenefitProgramCategory } from 'Utils/types/basicBenefitProgramCategory'

export const getEmployerBenefitProgramDetails = async (
  employerId: number,
  monthlyUtilization: boolean
): Promise<EmployerBenefitProgramsResponse> => {
  const response = await getRequest<EmployerBenefitProgramsApiResponse>(
    `/employers/${employerId}/benefit_program_details?monthly_utilization=${monthlyUtilization}`
  )

  return {
    statusCode: response.statusCode,
    success: response.success ?? false,
    message: response.message ?? '',
    data:
      response.success && response.data?.benefit_programs
        ? {
            benefitPrograms: parseEmployerBenefitProgramsResponseData(
              response.data.benefit_programs
            )
          }
        : undefined
  }
}

const parsePolicyDates = (
  apiPolicy: Omit<BenefitProgramPolicy, 'scheduledStartDate' | 'scheduledEndDate'> & {
    scheduledStartDate: string
    scheduledEndDate: string | null
  }
): BenefitProgramPolicy => ({
  ...apiPolicy,
  scheduledStartDate: parseServerDate(apiPolicy.scheduledStartDate),
  scheduledEndDate: apiPolicy.scheduledEndDate
    ? parseServerDate(apiPolicy.scheduledEndDate)
    : undefined
})

const parseEmployerBenefitProgramsResponseData = (
  benefitPrograms: ApiEmployerBenefitProgram[]
): EmployerBenefitProgram[] => {
  return (
    benefitPrograms
      .filter((bp) => bp.policy?.scheduledStartDate)
      .map((bp) => ({
        ...bp,
        policy: parsePolicyDates(bp.policy),
        futurePolicy: bp.futurePolicy ? parsePolicyDates(bp.futurePolicy) : undefined
      })) ?? []
  )
}

export const getBenefitProgramUtilization = async (
  employerId: number
): Promise<BenefitProgramUtiilizationResponse> => {
  const response = await getRequest<BenefitProgramUtilizationApiResponse>(
    `/employers/${employerId}/benefit_program_utilization`
  )

  return {
    statusCode: response.statusCode,
    success: response.success ?? false,
    message: response.message ?? '',
    data:
      response.success && response.data?.benefit_program_utilization
        ? {
            benefitProgramUtilization: response.data.benefit_program_utilization
          }
        : undefined
  }
}

type BenefitProgramWithCategoriesApiResponseData = {
  benefit_program: any
  basic_benefit_program_categories: BasicBenefitProgramCategory[]
}

type BenefitProgramWithCategoriesApiResponse =
  RequestResponse<BenefitProgramWithCategoriesApiResponseData>

export const getBenefitProgramWithCategories = async (
  benefitProgramId: number
): Promise<BasicBenefitProgramCategory[]> => {
  const response = await getRequest<BenefitProgramWithCategoriesApiResponse>(
    `/admin/benefit_programs/${benefitProgramId}`
  )

  if (!response.success || !response.data) {
    throw new Error(response.message || 'Failed to fetch benefit program categories')
  }

  return response.data.basic_benefit_program_categories
}
